import React, { useState, useEffect, ReactNode } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { connect } from 'react-redux'

import { useForTablet, useForBigScreen } from '@sketch/components'
import { SidebarLayout } from 'layouts/SidebarLayout'

import { isCwvRouteOnly } from 'utils/route'
import { actions as sidebarActions } from 'redux/modules/documentSidebar'

export interface DocumentSidebarLayoutHeaderProps {
  isSidebarRightOpen: boolean
  toggleSidebarRight: () => void
  isSidebarLeftOpen: boolean
  setSidebarLeftOpen: React.Dispatch<React.SetStateAction<boolean>>
}

type DocumentSidebarProps = {
  title?: string
  children: ReactNode
  footer?: ReactNode
  header: React.FC<DocumentSidebarLayoutHeaderProps> | ReactNode
  isSidebarRightOpen?: boolean
  /** Navigation sidebar */
  sidebarLeft?: ReactNode
  /** Activity/inspector sidebar */
  sidebarRight?: ReactNode
  toggleSidebarRight?: () => void
  darkBackground?: boolean
}

const DocumentSidebar = ({
  isSidebarRightOpen,
  toggleSidebarRight,
  ...rest
}: DocumentSidebarProps) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const isTabletAndBigger = useForTablet()
  const isDesktopAndBigger = useForBigScreen()
  const toggleMobileSidebar = () => setIsMobileSidebarOpen(isOpen => !isOpen)

  // For Components Web View pages on tablet we hide the sidebar because we
  // prioritise the left menu sidebar
  const { path } = useRouteMatch()
  const shouldHideRightSidebar =
    isTabletAndBigger && !isDesktopAndBigger && isCwvRouteOnly(path)

  useEffect(() => {
    if (isSidebarRightOpen && shouldHideRightSidebar) {
      toggleSidebarRight?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isOpen = isTabletAndBigger ? isSidebarRightOpen : isMobileSidebarOpen
  const toggle = isTabletAndBigger ? toggleSidebarRight : toggleMobileSidebar

  return (
    <SidebarLayout
      isSidebarRightOpen={isOpen}
      toggleSidebarRight={toggle}
      {...rest}
    />
  )
}

type MapStateToProps = {
  sidebarRightOpen?: boolean
}

const mapStateToProps = (state: MapStateToProps) => ({
  isSidebarRightOpen: state?.sidebarRightOpen,
})

const mapDispatchToProps = (
  dispatch: (args: { type: string; payload: null }) => void
) => ({
  toggleSidebarRight: () => dispatch(sidebarActions.toggleSidebarRight()),
})

export default connect(mapStateToProps, mapDispatchToProps)(DocumentSidebar)
